import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';

import {assert} from 'Asserts';
import {resolveLineageStep} from 'pipelines/PipelineViewUtils';
import {FONT_PROXIMA_16} from 'styles/FontConstants';
import {GRAY_DARK, GRAY_LIGHT} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {HALTED_VIEW_ICON} from './AnalysisViewUtils';
import DataViewContext, {useDataViewContext} from './DataViewContext';
import DataViewPane from './DataViewPane';

/**
 * Render view for steps that have been halted.
 *
 * Displays a halted icon with a title and optional status message from the step.
 *
 * @package
 */
export default function HaltedView(): JSX.Element {
  const dataViewContextValue = useDataViewContext();
  const {analysisView, steps} = dataViewContextValue;

  assert(analysisView != null, 'analysisView must be provided in context when using HaltedView');

  const lastNode = analysisView.lineage[analysisView.lineage.length - 1];
  const resolvedStep =
    lastNode != null && steps != null && steps.length > 0 ? resolveLineageStep(steps, lastNode) : null;
  const statusMessage = resolvedStep?.statusMessage;

  const newDataViewContextValue = useMemo(
    () => ({
      ...dataViewContextValue,
      dataTestAttrs: {component: 'HaltedView', element: 'root'},
    }),
    [dataViewContextValue]
  );

  return (
    <DataViewContext.Provider value={newDataViewContextValue}>
      <DataViewPane>
        <div className={css(styles.haltedViewWrapper)}>
          <BenchlingIcon icon={HALTED_VIEW_ICON} className={css(styles.icon)} />
          <p className={css(styles.titleText)}>Halted</p>
          {statusMessage != null && <p className={css(styles.titleText)}>{statusMessage}</p>}
        </div>
      </DataViewPane>
    </DataViewContext.Provider>
  );
}

const styles = StyleSheet.create({
  icon: {
    color: GRAY_LIGHT,
    height: '60px',
    width: '60px',
    marginBottom: 20,
  },
  haltedViewWrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%',
  },
  titleText: {
    ...FONT_PROXIMA_16,
    color: GRAY_DARK,
  },
});
